import { ArrowUp } from 'lucide-react';
import { formatNumber, formatClockTime, actualDepthM, nearestWaterLevel } from '@boatnav/core';
import { useWeatherStore, WEATHER_DIM_AGE_MS, WEATHER_HIDE_AGE_MS } from './weatherStore.js';
import { useTideStore, TIDE_DIM_AGE_MS, TIDE_HIDE_AGE_MS } from './tideStore.js';
import { ageMs } from './age.js';
import { useBoatStore, activeBoat } from '../boat/boatStore.js';
import { WeatherSymbolIcon } from './weatherSymbols.js';
import { TideCurve } from './TideCurve.js';
import { msg } from '../../lib/messages.js';
import './conditions.css';

const HOURS_SHOWN = 24;

function signedCm(cm: number): string {
  return `${cm >= 0 ? '+' : ''}${formatNumber(cm, 0)} cm`;
}

function UpdatedLabel({ fetchedAt, dimAfterMs }: { fetchedAt: number | null; dimAfterMs: number }): JSX.Element | null {
  if (fetchedAt == null) return null;
  const age = ageMs(fetchedAt) ?? 0;
  return (
    <p className={`conditions-sheet__updated${age > dimAfterMs ? ' conditions-sheet__updated--stale' : ''}`}>
      {msg.weather.updated} <span className="numeral">{formatClockTime(fetchedAt)}</span>
    </p>
  );
}

function WeatherSection(): JSX.Element | null {
  const data = useWeatherStore((s) => s.data);
  const fetchedAt = useWeatherStore((s) => s.fetchedAt);
  const age = ageMs(fetchedAt);
  if (!data || (age != null && age > WEATHER_HIDE_AGE_MS)) return null;

  const hours = data.hours.slice(0, HOURS_SHOWN);

  return (
    <section className="conditions-sheet__section">
      <h3 className="conditions-sheet__section-title">{msg.weather.title}</h3>
      <div className="conditions-sheet__table-wrap">
        <table className="weather-table">
          <thead>
            <tr>
              <th>{msg.weather.hour}</th>
              <th aria-hidden="true" />
              <th>{msg.weather.wind}</th>
              <th>{msg.weather.temp}</th>
              <th>{msg.weather.precip}</th>
            </tr>
          </thead>
          <tbody>
            {hours.map((h) => (
              <tr key={h.ts}>
                <td className="numeral">{formatClockTime(h.ts)}</td>
                <td>
                  <WeatherSymbolIcon symbolCode={h.symbol} size={16} />
                </td>
                <td className="numeral weather-table__wind">
                  <ArrowUp
                    size={11}
                    style={{ transform: `rotate(${h.windFromDeg + 180}deg)` }}
                    aria-hidden="true"
                  />
                  {formatNumber(h.windMs, 0)}
                  {h.windGustMs != null ? ` (${formatNumber(h.windGustMs, 0)})` : ''}
                </td>
                <td className="numeral">{formatNumber(h.airTempC, 0)}°</td>
                <td className="numeral">{h.precipMm != null ? formatNumber(h.precipMm, 1) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UpdatedLabel fetchedAt={fetchedAt} dimAfterMs={WEATHER_DIM_AGE_MS} />
    </section>
  );
}

function TideSection(): JSX.Element | null {
  const data = useTideStore((s) => s.data);
  const fetchedAt = useTideStore((s) => s.fetchedAt);
  const boat = useBoatStore(activeBoat);
  const age = ageMs(fetchedAt);
  if (!data || (age != null && age > TIDE_HIDE_AGE_MS)) return null;

  const now = Date.now();
  const nowSample = nearestWaterLevel(data.series, now);

  return (
    <section className="conditions-sheet__section">
      <h3 className="conditions-sheet__section-title">{msg.tide.title}</h3>

      {nowSample && (
        <p className="conditions-sheet__headline numeral">
          {msg.tide.now}: <b>{signedCm(nowSample.cm)}</b> {msg.tide.aboveDatum}
        </p>
      )}

      {(data.nextHigh || data.nextLow) && (
        <p className="conditions-sheet__events numeral">
          {data.nextHigh && `${msg.tide.high} ${formatClockTime(data.nextHigh.ts)} (${signedCm(data.nextHigh.cm)})`}
          {data.nextHigh && data.nextLow && ' · '}
          {data.nextLow && `${msg.tide.low} ${formatClockTime(data.nextLow.ts)} (${signedCm(data.nextLow.cm)})`}
        </p>
      )}

      <TideCurve series={data.series} nowMs={now} />

      {boat && nowSample && (
        <p className="conditions-sheet__depth">
          {msg.tide.depthPrefix} et {formatNumber(boat.draughtM, 1)} m-grunt har nå ≈{' '}
          <b className="numeral">{formatNumber(actualDepthM(boat.draughtM, nowSample.cm), 1)} m</b> vann.
        </p>
      )}

      <UpdatedLabel fetchedAt={fetchedAt} dimAfterMs={TIDE_DIM_AGE_MS} />
    </section>
  );
}

export function ConditionsSheet({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet conditions-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={msg.conditions.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__handle" />
        <h2 className="sheet__title">{msg.conditions.title}</h2>

        <div className="conditions-sheet__scroll">
          <WeatherSection />
          <TideSection />
        </div>

        <p className="conditions-sheet__attribution">
          {msg.weather.attribution}
          <br />
          {msg.tide.attribution}
        </p>

        <button type="button" className="btn btn--ghost conditions-sheet__close" onClick={onClose}>
          {msg.common.back}
        </button>
      </div>
    </div>
  );
}
